import React, { useEffect } from 'react';
import { isEmpty, filter, startCase, isEqual } from 'lodash';
import {
  FinanceResponseData,
  Insurance,
  InsuranceAddress,
  InsuranceType,
  InsuranceTypeLabelMap,
} from '@upstart/harmony/lib/legos/financingTypes';
import { Checkbox } from 'react-bootstrap';
import { parseCurrencyTruncated } from '@upstart/harmony/lib/form-utils';
import { parseNumberIfPresent } from '@upstart/harmony/lib/ric-form-utils';
import Input, { PriceInput } from '../../../../components/PencilEditor/Input';
import './styles.scss';
import { FormTable } from './FormItems';

type InsuranceColumn = {
  title?: string;
  width: string;
  fieldName: keyof Insurance;
};

const ADDRESS_LABEL = 'Address';
const INSURANCE_COMPANY_NAME_LABEL = 'Insurance Company Name';
const BLANK_ADDRESS = {
  lineOne: '',
  lineTwo: '',
  city: '',
  stateCode: '',
  zip: '',
};

const hasFullAddress = (insurance?: Insurance) =>
  !isEmpty(insurance?.address?.lineOne) &&
  !isEmpty(insurance?.address?.city) &&
  !isEmpty(insurance?.address?.stateCode) &&
  !isEmpty(insurance?.address?.zip);

export function InsuranceTable(props: {
  optionalColumns: InsuranceColumn[];
  insuranceTypes: InsuranceType[];
  insurances: Insurance[];
  onChange: (data: Partial<FinanceResponseData>) => void;
}) {
  const { optionalColumns, insuranceTypes, insurances, onChange } = props;

  const hasLifeAndDisability =
    insuranceTypes.includes(InsuranceType.CREDIT_LIFE) &&
    insuranceTypes.includes(InsuranceType.CREDIT_DISABILITY);

  // returns whether life+disability both exist and if this insurance is one of the types
  const isSpecialLifeOrDisabilityField = (insuranceType: InsuranceType) =>
    hasLifeAndDisability &&
    (insuranceType === InsuranceType.CREDIT_LIFE ||
      insuranceType === InsuranceType.CREDIT_DISABILITY);

  const getInsuranceByType = (insuranceType: InsuranceType) =>
    insurances.find(i => i.type === insuranceType);
  const creditLife = getInsuranceByType(InsuranceType.CREDIT_LIFE);
  const creditDisability = getInsuranceByType(InsuranceType.CREDIT_DISABILITY);

  const getFieldValue = (column: InsuranceColumn, val: string) => {
    if (['termMonths'].includes(column.fieldName)) {
      return parseNumberIfPresent(val);
    }
    return val;
  };

  const updateInsurances = ({
    insurance,
    newData,
  }: {
    insurance?: Insurance;
    newData: Partial<Insurance>;
  }) => {
    const insurancesList = [...(insurances ?? [])];
    const index = insurancesList.findIndex(i => i === insurance);
    if (index >= 0) {
      insurancesList[index] = { ...insurancesList[index], ...newData };
    }
    return insurancesList;
  };

  const updateCreditLifeAndDisabilityInsurances = ({
    newData,
  }: {
    newData: Partial<Insurance>;
  }) => {
    const insurancesList = [...(insurances ?? [])].map(i => {
      if (isSpecialLifeOrDisabilityField(i.type)) {
        return { ...i, ...newData };
      }
      return i;
    });
    return insurancesList;
  };

  const toggleActiveInsurance = (
    insuranceType: InsuranceType,
    value: boolean,
  ) => {
    let updatedInsuranceList: Insurance[];

    if (value) {
      // special handle life/disability fields by setting the initial value to the other
      if (isSpecialLifeOrDisabilityField(insuranceType)) {
        const otherInsurance = creditLife ?? creditDisability;
        updatedInsuranceList = [
          ...insurances,
          {
            type: insuranceType,
            insuranceCompanyName: otherInsurance?.insuranceCompanyName ?? '',
            premium: 0,
            address: otherInsurance?.address ?? BLANK_ADDRESS,
          },
        ];
      } else {
        updatedInsuranceList = [
          ...insurances,
          {
            type: insuranceType,
            insuranceCompanyName: '',
            premium: 0,
            address: { ...BLANK_ADDRESS },
          },
        ];
      }
    } else {
      updatedInsuranceList = filter(insurances, i => i.type !== insuranceType);
    }
    onChange({
      insurances: updatedInsuranceList,
    });
  };

  // On init: If both life and disability insurances exist, set their addresses and insurance names to the same values
  useEffect(() => {
    if (
      hasLifeAndDisability &&
      creditLife &&
      creditDisability &&
      !isEqual(creditLife.address, creditDisability.address) &&
      !isEqual(
        creditLife.insuranceCompanyName,
        creditDisability.insuranceCompanyName,
      )
    ) {
      const insurance = hasFullAddress(creditLife)
        ? creditLife
        : creditDisability;
      onChange({
        insurances: updateCreditLifeAndDisabilityInsurances({
          newData: {
            insuranceCompanyName: insurance?.insuranceCompanyName ?? '',
            address: insurance?.address ?? BLANK_ADDRESS,
          },
        }),
      });
    }
  }, [insurances]);

  // render components

  // renders the first line of the insurance section, including any optional field columns and the price
  const renderRowOne = (
    insuranceType: InsuranceType,
    insurance?: Insurance,
  ) => {
    const columns: InsuranceColumn[] = [
      {
        title: `${InsuranceTypeLabelMap?.[insuranceType] ??
          insuranceType} Insurance`,
        width: '300px',
        fieldName: 'type',
      },
      ...optionalColumns,
    ];

    const getDescriptionField = (column: InsuranceColumn) => {
      if (column.fieldName === 'type') {
        return (
          <div style={{ fontWeight: 'normal', fontStyle: 'italic' }}>
            <Checkbox
              reversed
              id={`${insuranceType}-checkbox`}
              className={'insurance-active-checkbox'}
              name={`${insuranceType}-checkbox`}
              checked={!!insurance}
              onChange={() => {
                toggleActiveInsurance(insuranceType, !insurance);
              }}
            />
          </div>
        );
      }
      return (
        <Input
          value={insurance?.[column.fieldName]}
          placeholder="N/A"
          required={insurance}
          disabled={!insurance}
          onChange={(val: string) =>
            onChange({
              insurances: updateInsurances({
                insurance,
                newData: {
                  [column.fieldName]: getFieldValue(column, val),
                },
              }),
            })
          }
        />
      );
    };

    return (
      <FormTable
        columns={columns}
        rows={[
          {
            descriptions: columns.map((column, i) =>
              getDescriptionField(column),
            ),
            value: (
              <PriceInput
                value={insurance?.premium}
                placeholder="N/A"
                disabled={!insurance}
                onChange={(val: string) =>
                  onChange({
                    insurances: updateInsurances({
                      insurance,
                      newData: {
                        premium: parseCurrencyTruncated(val),
                      },
                    }),
                  })
                }
              />
            ),
          },
        ]}
      />
    );
  };

  // renders the company name input field
  const renderCompanyName = ({
    insurance,
    customUpdateInsurances,
  }: {
    insurance?: Insurance;
    customUpdateInsurances?: Function;
  }) => (
    <div className="form-section-line-item">
      <div className="insurance-field">
        <div className={'insurance-field-label'}>
          {INSURANCE_COMPANY_NAME_LABEL}
        </div>
        <Input
          className={`insurance-insuranceCompanyName-input`}
          value={insurance?.insuranceCompanyName}
          placeholder="N/A"
          disabled={!insurance}
          required={insurance}
          onChange={(val: string) =>
            onChange({
              insurances: (customUpdateInsurances || updateInsurances)({
                insurance,
                newData: {
                  insuranceCompanyName: val,
                },
              }),
            })
          }
        />
      </div>
    </div>
  );

  const renderAddressFields = ({
    insurance,
    customUpdateInsurances,
  }: {
    insurance?: Insurance;
    customUpdateInsurances?: Function;
  }) => {
    const address = {
      ...BLANK_ADDRESS,
      lineOne: insurance?.address?.lineOne ?? BLANK_ADDRESS.lineOne,
      lineTwo: insurance?.address?.lineTwo ?? BLANK_ADDRESS.lineTwo,
      city: insurance?.address?.city ?? BLANK_ADDRESS.city,
      stateCode: insurance?.address?.stateCode ?? BLANK_ADDRESS.stateCode,
      zip: insurance?.address?.zip ?? BLANK_ADDRESS.zip,
    };
    const onInsuranceChange = customUpdateInsurances || updateInsurances;

    const addressInputFields = Object.keys(address).map((key: string) => {
      const field = key as keyof InsuranceAddress;
      const attributes = field === 'stateCode' ? { maxLength: 2 } : {};
      return (
        <div className="insurance-address-field">
          <div className="insurance-address-label">{startCase(field)}</div>
          <Input
            {...attributes}
            className={`insurance-address-input insurance-address-${field}-input`}
            value={address[field]}
            placeholder="N/A"
            disabled={!insurance}
            required={insurance && field !== 'lineTwo'}
            onChange={(val: string) =>
              onChange({
                insurances: onInsuranceChange({
                  insurance,
                  newData: {
                    address: { ...address, [field]: val },
                  },
                }),
              })
            }
          />
        </div>
      );
    });

    return (
      <>
        <div className="form-section-line-item">
          <div className="form-section-line-item-description">
            <div className={'insurance-field-label'}>{ADDRESS_LABEL}</div>
          </div>
        </div>
        {addressInputFields}
      </>
    );
  };

  const renderLifeAndDisabilityInsuranceFields = () => (
    <>
      {renderRowOne(InsuranceType.CREDIT_LIFE, creditLife)}
      {renderRowOne(InsuranceType.CREDIT_DISABILITY, creditDisability)}
      <div className="form-subsection">
        {renderCompanyName({
          insurance: creditLife ?? creditDisability,
          customUpdateInsurances: updateCreditLifeAndDisabilityInsurances,
        })}
        {renderAddressFields({
          insurance: creditLife ?? creditDisability,
          customUpdateInsurances: updateCreditLifeAndDisabilityInsurances,
        })}
      </div>
    </>
  );

  const renderOtherInsuranceFields = () =>
    insuranceTypes
      .filter(i => !isSpecialLifeOrDisabilityField(i))
      .map((insuranceType, index) => {
        const insurance = getInsuranceByType(insuranceType);
        return (
          <>
            {renderRowOne(insuranceType, insurance)}
            <div className="form-subsection">
              {renderCompanyName({
                insurance,
              })}
              {renderAddressFields({
                insurance,
              })}
            </div>
          </>
        );
      });

  return (
    <>
      {hasLifeAndDisability && renderLifeAndDisabilityInsuranceFields()}
      {renderOtherInsuranceFields()}
    </>
  );
}

export default InsuranceTable;
